import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "academic_policy_contribution";

const AcademicPolicyContribution = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [recordId, setRecordId] = useState(null);

  const [formData, setFormData] = useState({
    starting_date: null,
    ending_date: null,
    unit: "",
    role: "",
    nature: "",
    committee_name: "",
    country: "",
    institution: "",
  });

 const fetchRecords = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to fetch records");

    const data = await res.json();

    const processed = Array.isArray(data)
      ? data
          .map((rec) => ({
            ...rec,
            starting_date: rec.starting_date
              ? new Date(rec.starting_date)
              : null,
            ending_date: rec.ending_date
              ? new Date(rec.ending_date)
              : null,
          }))
          .sort((a, b) => {
            if (!a.starting_date) return 1; // Empty dates go to the bottom
            if (!b.starting_date) return -1;
            return b.starting_date - a.starting_date; // Latest first
          })
      : [];

    setRecords(processed);
  } catch (err) {
    console.error(err);
    toast.error(`Fetch error: ${err.message}`);
  }
};

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        starting_date: formData.starting_date
          ? `${formData.starting_date.getFullYear()}-${String(
              formData.starting_date.getMonth() + 1
            ).padStart(2, "0")}-01`
          : null,

        ending_date: formData.ending_date
          ? `${formData.ending_date.getFullYear()}-${String(
              formData.ending_date.getMonth() + 1
            ).padStart(2, "0")}-01`
          : null,
      };

      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;
      const method = recordId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save record");

      toast.success(
        recordId ? "Record updated successfully!" : "Record added successfully!"
      );
      setFormData({
        starting_date: null,
        ending_date: null,
        unit: "",
        role: "",
        nature: "",
        committee_name: "",
      });
      setRecordId(null);
      fetchRecords();
    } catch (err) {
      console.error(err);
      toast.error(`Submit error: ${err.message}`);
    }
  };

  const handleEdit = (rec) => {
    setFormData({
      starting_date: rec.starting_date ? new Date(rec.starting_date) : null,
      ending_date: rec.ending_date ? new Date(rec.ending_date) : null,
      unit: rec.unit || "",
      role: rec.role || "",
      nature: rec.nature || "",
      institution: rec.institutioni || "",
      country: rec.country || "",
    });
    setRecordId(rec.id);
    toast.info("Editing record...");
  };

  const handleDelete = (rec) => {
    const toastId = toast.info(
      <div>
        Delete this record?{" "}
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${rec.id}`,
                {
                  method: "DELETE",
                  credentials: "include",
                }
              );
              if (!res.ok) throw new Error("Failed to delete record");
              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
                closeButton: true,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 3000,
                closeButton: true,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  const inputStyle = {
    padding: "10px",
    marginTop: "5px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    width: "100%",
    fontSize: "16px",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />

      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Contribution to Academic Policy
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Duration */}
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>
                Starting Date (mm/yyyy)
              </label>
              <DatePicker
                selected={formData.starting_date}
                onChange={(date) =>
                  setFormData({ ...formData, starting_date: date })
                }
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>
                Ending Date (mm/yyyy)
              </label>
              <DatePicker
                selected={formData.ending_date}
                onChange={(date) =>
                  setFormData({ ...formData, ending_date: date })
                }
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
                style={inputStyle}
              />
            </div>
          </div>

          {[
            { label: "Unit of Contribution", name: "unit", type: "select" },
            { label: "Role", name: "role", type: "select" },
            { label: "Nature of Contribution", name: "nature" },
            { label: "Committee / Policy Body Name", name: "committee_name" },
            { label: "Institution", name: "institution" },
            { label: "Country", name: "country" },
          ].map((field) => (
            <div key={field.name}>
              <label style={{ fontWeight: "bold", marginTop: "10px" }}>
                {field.label}
              </label>
              {field.type === "select" && field.name === "unit" ? (
                <select
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  style={inputStyle}
                  required
                >
                  <option value="">Select Unit</option>
                  <option value="University-level">University-level</option>
                  <option value="School-level">School-level</option>
                  <option value="Discipline/Area/Department-level">
                    Discipline/Area/Department-level
                  </option>
                </select>
              ) : field.type === "select" ? (
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  style={inputStyle}
                  required
                >
                  <option value="">Select Role</option>
                  <option value="Lead">Lead</option>
                  <option value="Member">Member</option>
                </select>
              ) : (
                <input
                  type="text"
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleChange}
                  style={inputStyle}
                  required
                />
              )}
            </div>
          ))}

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>Your Records</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Duration",
                    "Unit",
                    "Role",
                    "Nature",
                    "Institution",
                    "Country",
                    "Committee",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: "#f1f5f9",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.starting_date
                        ? `${format(
                            new Date(rec.starting_date),
                            "MM/yyyy"
                          )} - ${
                            rec.ending_date
                              ? format(new Date(rec.ending_date), "MM/yyyy")
                              : "Present"
                          }`
                        : ""}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.unit}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.role}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.nature}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.committee_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.institution}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      <div style={{ display: "inline-flex", gap: "5px" }}>
                        <button
                          onClick={() => handleEdit(rec)}
                          style={{
                            background: "#2563eb",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            padding: "5px 10px",
                            cursor: "pointer",
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(rec)}
                          style={{
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            padding: "5px 10px",
                            cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcademicPolicyContribution;
